import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Check, Sparkles } from 'lucide-react';
import { pricingPlans } from '../data/mock';

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  return (
    <section className="py-24 bg-white">
      <div className="container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={springConfig}
          className="text-center mb-12"
        >
          <h2 
            className="heading-large mb-4"
            style={{
              fontSize: 'clamp(32px, 4vw, 48px)',
              fontWeight: 600,
              color: '#0A0A0A',
              letterSpacing: '-0.01em'
            }}
          >
            Simple, Transparent Pricing
          </h2>
          <p 
            className="body-large max-w-2xl mx-auto mb-8"
            style={{
              fontSize: '18px',
              color: '#3C3B3D',
              lineHeight: 1.6
            }}
          >
            Start free. Upgrade when you need more. Cancel anytime.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-3 bg-gray-100 rounded-full p-1.5">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'annual'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Annual
              <span className="px-2 py-0.5 bg-gradient-to-r from-orange-400 to-pink-400 text-white text-xs rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {pricingPlans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ ...springConfig, delay: index * 0.15 }}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
              className={`rounded-3xl p-8 relative ${
                plan.popular
                  ? 'bg-gradient-to-br from-orange-50 to-pink-50 border-2 border-orange-200 shadow-xl'
                  : 'bg-white border border-gray-200 shadow-sm'
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div 
                    className="px-4 py-1.5 rounded-full flex items-center gap-1 text-white text-sm font-medium"
                    style={{
                      background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)'
                    }}
                  >
                    <Sparkles className="w-4 h-4" />
                    Most Popular
                  </div>
                </div>
              )}

              {/* Plan Name */}
              <h3 
                className="heading-medium mb-2"
                style={{
                  fontSize: '24px',
                  fontWeight: 600,
                  color: '#0A0A0A'
                }}
              >
                {plan.name}
              </h3>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span 
                    className="text-5xl font-bold"
                    style={{ color: '#0A0A0A' }}
                  >
                    ₹{billingCycle === 'annual' && plan.annualPrice 
                      ? plan.annualPrice 
                      : plan.price}
                  </span>
                  <span className="text-gray-600">
                    /{billingCycle === 'annual' && plan.annualPeriod 
                      ? plan.annualPeriod 
                      : plan.period}
                  </span>
                </div>
                {billingCycle === 'annual' && plan.savings && (
                  <p className="text-sm text-green-600 font-medium mt-1">{plan.savings}</p>
                )}
              </div>

              {/* CTA Button */}
              <a
                href={plan.ctaUrl || "https://play.google.com/store/apps/details?id=com.typeit4u.keyboard"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full mb-6 block text-center"
                style={{
                  background: plan.popular 
                    ? 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)'
                    : 'transparent',
                  color: plan.popular ? 'white' : '#0A0A0A',
                  border: plan.popular ? 'none' : '1px solid #E6E4EB',
                  borderRadius: '26px',
                  padding: '16px',
                  fontSize: '16px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  textDecoration: 'none'
                }}
              >
                {plan.cta}
              </a>

              {/* Features List */}
              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-900 mb-3">What's included:</p>
                {plan.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <Check 
                      className="w-5 h-5 flex-shrink-0 mt-0.5"
                      style={{ color: plan.popular ? '#FF6B9D' : '#10b981' }}
                    />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust Statement */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ ...springConfig, delay: 0.3 }}
          className="text-center mt-12"
        >
          <p className="text-sm text-gray-600">
            All plans include 1-day free trial • Cancel anytime • No credit card required
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
